import { useQuery } from 'data-fetching-client';
import { useCallback, useEffect, useMemo } from 'react';
import { GET_GROWTH_PURCHASE_OPTIONS_V2_QUERY, GET_IN_APP_GROWTH_PURCHASE_OPTIONS_FOR_CART_QUERY } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc.dfc.new';
import { useRegisterBannerPromotionIds } from '../../context/BannerContext';
import { logApiError, logApiSuccess } from '../../utils/apiLogging';
import { getBrowserLocale } from '../../../utils/getBrowserLocale';
import { useDisplayedCurrencyCode } from './useCurrencyDetails';

/**
 * Base hook for querying growth pop purchase options. This should not be used directly,
 * instead use one of the more specific hooks below.
 * @param productApiNames - The product API names to query
 * @returns The products, currency code, error, and loading state
 */
export const useGrowthPopPurchaseOptions = ({
  productsToPurchase,
  skip
}) => {
  const version = 'v2';
  const currencyCode = useDisplayedCurrencyCode();
  const queryName = 'GET_GROWTH_PURCHASE_OPTIONS_V2_QUERY';
  const handleCompleted = useCallback(() => {
    logApiSuccess({
      queryName,
      operation: 'getGrowthPurchaseOptions',
      version
    });
  }, [queryName, version]);
  const handleError = useCallback(error => {
    logApiError(error, {
      queryName,
      operation: 'getGrowthPurchaseOptions',
      version
    });
  }, [queryName, version]);
  const {
    browserLocale,
    preferredCountryCode
  } = getBrowserLocale();
  const {
    data,
    error,
    loading
  } = useQuery(GET_GROWTH_PURCHASE_OPTIONS_V2_QUERY, {
    variables: {
      products: productsToPurchase,
      currencyCode,
      browserLocale,
      preferredCountry: preferredCountryCode
    },
    skip: skip || !currencyCode,
    onCompleted: handleCompleted,
    onError: handleError
  });
  const products = data === null || data === void 0 ? void 0 : data.result.products;
  const register = useRegisterBannerPromotionIds();

  // Note: The BannerContext is an append-only accumulator — promotion IDs are
  // never removed once registered, even if a subsequent GrowthPOP response
  // omits them. This is intentional; banners are shown if eligibility was
  // observed in *any* response during the session.
  useEffect(() => {
    if (!products || !register) return;
    const ids = [];
    for (const product of Object.values(products)) {
      const options = product.purchaseOptions;
      if (!options) continue;
      for (const purchaseOption of Object.values(options)) {
        const discounts = purchaseOption.discounts;
        if (!discounts) continue;
        for (const discount of discounts) {
          if (discount.promotionId) {
            ids.push(discount.promotionId);
          }
        }
      }
    }
    register(ids);
  }, [products, register]);
  return {
    products,
    currencyCode: data === null || data === void 0 ? void 0 : data.result.currencyCode,
    error,
    loading
  };
};

/**
 * Returns the best price for a list of products.
 *
 * @param productsToPurchase - Array of products to purchase (productApiName, quantity)
 * @returns The best prices for each product, currency code, error, and loading state
 */
export const useBestPriceForProducts = ({
  productsToPurchase
}) => {
  const {
    products,
    error,
    loading,
    currencyCode
  } = useGrowthPopPurchaseOptions({
    productsToPurchase
  });
  const bestPriceMap = useMemo(() => {
    if (loading) {
      return undefined;
    }
    if (!products) {
      return null;
    }
    const pricesMap = {};
    for (const productApiName of Object.keys(products)) {
      if (products[productApiName].bestPrice) {
        var _products$productApiN, _products$productApiN2, _products$productApiN3, _products$productApiN4;
        const monthlyPrice = (_products$productApiN = products[productApiName].bestPrice) !== null && _products$productApiN !== void 0 && _products$productApiN.monthlyPrice ? Number((_products$productApiN2 = products[productApiName].bestPrice) === null || _products$productApiN2 === void 0 ? void 0 : _products$productApiN2.monthlyPrice) : loading ? undefined : null;
        const priceForCommittedPeriod = (_products$productApiN3 = products[productApiName].bestPrice) !== null && _products$productApiN3 !== void 0 && _products$productApiN3.priceForCommittedPeriod ? Number((_products$productApiN4 = products[productApiName].bestPrice) === null || _products$productApiN4 === void 0 ? void 0 : _products$productApiN4.priceForCommittedPeriod) : loading ? undefined : null;
        pricesMap[productApiName] = {
          monthlyPrice,
          priceForCommittedPeriod
        };
      }
    }
    return pricesMap;
  }, [products, loading]);
  return {
    bestPriceMap,
    error,
    loading,
    currencyCode
  };
};

/**
 * Returns all per month prices for a product.
 *
 * @param productApiName - The product API name to query
 * @param quantity - The quantity of the product to purchase
 * @returns The annual and monthly prices, currency code, error, and loading state
 */
export const usePerMonthPricesForRecurringTerms = ({
  productApiName,
  quantity = 1,
  skip
}) => {
  var _products$productApiN5, _annualPurchaseOption, _annualPurchaseOption2, _products$productApiN6, _monthlyPurchaseOptio, _monthlyPurchaseOptio2;
  const response = useGrowthPopPurchaseOptions({
    productsToPurchase: [{
      productApiName,
      quantity
    }],
    skip
  });
  const {
    products,
    currencyCode,
    error,
    loading
  } = response;

  // Return null if loading is complete but product is unavailable
  if (loading) {
    return {
      annualPrice: undefined,
      annualDiscount: undefined,
      annualListPrice: undefined,
      monthlyPrice: undefined,
      monthlyDiscount: undefined,
      monthlyListPrice: undefined
    };
  }
  if (!products) {
    return {
      annualPrice: null,
      annualDiscount: null,
      annualListPrice: null,
      monthlyPrice: null,
      monthlyDiscount: null,
      monthlyListPrice: null
    };
  }
  const annualPurchaseOption = (_products$productApiN5 = products[productApiName]) === null || _products$productApiN5 === void 0 || (_products$productApiN5 = _products$productApiN5.purchaseOptions) === null || _products$productApiN5 === void 0 ? void 0 : _products$productApiN5.ANNUAL;
  const annualPrice = annualPurchaseOption !== null && annualPurchaseOption !== void 0 && annualPurchaseOption.monthlyPrice ? Number(annualPurchaseOption === null || annualPurchaseOption === void 0 ? void 0 : annualPurchaseOption.monthlyPrice) : loading ? undefined : null;
  const foundAnnualDiscount = annualPurchaseOption === null || annualPurchaseOption === void 0 || (_annualPurchaseOption = annualPurchaseOption.discounts) === null || _annualPurchaseOption === void 0 || (_annualPurchaseOption = _annualPurchaseOption.find(discount => discount.discountedAmountPerPurchase)) === null || _annualPurchaseOption === void 0 ? void 0 : _annualPurchaseOption.discountedAmountPerPurchase;
  const annualDiscount = foundAnnualDiscount ? Number(foundAnnualDiscount) : loading ? undefined : null;
  const annualListPrice = annualPurchaseOption !== null && annualPurchaseOption !== void 0 && (_annualPurchaseOption2 = annualPurchaseOption.listPrice) !== null && _annualPurchaseOption2 !== void 0 && _annualPurchaseOption2.monthlyPrice ? Number(annualPurchaseOption.listPrice.monthlyPrice) : loading ? undefined : null;
  const monthlyPurchaseOption = (_products$productApiN6 = products[productApiName]) === null || _products$productApiN6 === void 0 || (_products$productApiN6 = _products$productApiN6.purchaseOptions) === null || _products$productApiN6 === void 0 ? void 0 : _products$productApiN6.MONTHLY;
  const monthlyPrice = monthlyPurchaseOption !== null && monthlyPurchaseOption !== void 0 && monthlyPurchaseOption.monthlyPrice ? Number(monthlyPurchaseOption === null || monthlyPurchaseOption === void 0 ? void 0 : monthlyPurchaseOption.monthlyPrice) : loading ? undefined : null;
  const foundMonthlyDiscount = monthlyPurchaseOption === null || monthlyPurchaseOption === void 0 || (_monthlyPurchaseOptio = monthlyPurchaseOption.discounts) === null || _monthlyPurchaseOptio === void 0 || (_monthlyPurchaseOptio = _monthlyPurchaseOptio.find(discount => discount.discountedAmountPerPurchase)) === null || _monthlyPurchaseOptio === void 0 ? void 0 : _monthlyPurchaseOptio.discountedAmountPerPurchase;
  const monthlyDiscount = foundMonthlyDiscount ? Number(foundMonthlyDiscount) : loading ? undefined : null;
  const monthlyListPrice = monthlyPurchaseOption !== null && monthlyPurchaseOption !== void 0 && (_monthlyPurchaseOptio2 = monthlyPurchaseOption.listPrice) !== null && _monthlyPurchaseOptio2 !== void 0 && _monthlyPurchaseOptio2.monthlyPrice ? Number(monthlyPurchaseOption.listPrice.monthlyPrice) : loading ? undefined : null;
  return {
    annualPrice,
    annualDiscount,
    annualListPrice,
    monthlyPrice,
    monthlyDiscount,
    monthlyListPrice,
    currencyCode,
    error,
    loading
  };
};

/**
 * Returns the per month price for a product for a given term.
 *
 * @param productApiName - The product API name to query
 * @param quantity - The quantity of the product to purchase
 * @param term - The term to query
 * @returns The per month price, discount, fallback values, resolvedTerm used, currency code, error, and loading state
 */
export const usePerMonthPriceForTerm = ({
  productApiName,
  quantity = 1,
  term = 'ANNUAL',
  skip
}) => {
  const {
    annualPrice,
    annualDiscount,
    annualListPrice,
    monthlyPrice,
    monthlyDiscount,
    monthlyListPrice,
    currencyCode,
    error,
    loading
  } = usePerMonthPricesForRecurringTerms({
    productApiName,
    quantity,
    skip
  });
  const requestedPrice = term === 'ANNUAL' ? annualPrice : monthlyPrice;
  const requestedDiscount = term === 'ANNUAL' ? annualDiscount : monthlyDiscount;
  const requestedListPrice = term === 'ANNUAL' ? annualListPrice : monthlyListPrice;
  const resolvedTerm = loading ? undefined : requestedPrice != null ? term : annualPrice != null ? 'ANNUAL' : 'MONTHLY';
  const price = requestedPrice == null ? annualPrice || monthlyPrice : requestedPrice;
  const discount = requestedPrice == null ? annualDiscount || monthlyDiscount : requestedDiscount;
  const listPrice = requestedListPrice == null ? annualListPrice || monthlyListPrice : requestedListPrice;
  return {
    price,
    discount,
    listPrice,
    resolvedTerm,
    currencyCode,
    error,
    loading
  };
};

/**
 * Returns the promotion IDs for a product for a given term.
 *
 * @param productApiName - The product API name to query
 * @param quantity - The quantity of the product to purchase
 * @param term - The term to query
 * @returns The promotion IDs, error, and loading state
 */
const useProductPromotionIdsForTerm = ({
  productApiName,
  term = 'ANNUAL',
  quantity = 1
}) => {
  const {
    products,
    error,
    loading
  } = useGrowthPopPurchaseOptions({
    productsToPurchase: [{
      productApiName,
      quantity
    }]
  });
  const promotionIds = useMemo(() => {
    var _product$purchaseOpti, _purchaseOption$disco;
    if (loading) {
      return undefined;
    }
    if (!products) {
      return null;
    }
    const product = products[productApiName];
    if (!product) {
      return [];
    }
    const purchaseOption = (_product$purchaseOpti = product.purchaseOptions) === null || _product$purchaseOpti === void 0 ? void 0 : _product$purchaseOpti[term];
    if (!purchaseOption) {
      return [];
    }
    return ((_purchaseOption$disco = purchaseOption.discounts) === null || _purchaseOption$disco === void 0 ? void 0 : _purchaseOption$disco.map(discount => discount.promotionId)) || [];
  }, [loading, products, term, productApiName]);
  return {
    promotionIds,
    error,
    loading
  };
};

/**
 * Returns the promotion IDs for a product for all terms.
 *
 * @param productApiName - The product API name to query
 * @param quantity - The quantity of the product to purchase
 * @returns The promotion IDs, error, and loading state
 */
export const useProductPromotionIds = ({
  productApiName,
  quantity = 1
}) => {
  var _ref;
  const {
    promotionIds: annualPromotionIds,
    error: annualError,
    loading: annualLoading
  } = useProductPromotionIdsForTerm({
    productApiName,
    term: 'ANNUAL',
    quantity
  });
  const {
    promotionIds: monthlyPromotionIds,
    error: monthlyError,
    loading: monthlyLoading
  } = useProductPromotionIdsForTerm({
    productApiName,
    term: 'MONTHLY',
    quantity
  });
  const {
    promotionIds: oneTimeFeePromotionIds,
    error: oneTimeFeeError,
    loading: oneTimeFeeLoading
  } = useProductPromotionIdsForTerm({
    productApiName,
    term: 'ONE_TIME_FEE',
    quantity
  });
  return {
    promotionIds: [...(annualPromotionIds !== null && annualPromotionIds !== void 0 ? annualPromotionIds : []), ...(monthlyPromotionIds !== null && monthlyPromotionIds !== void 0 ? monthlyPromotionIds : []), ...(oneTimeFeePromotionIds !== null && oneTimeFeePromotionIds !== void 0 ? oneTimeFeePromotionIds : [])],
    error: (_ref = annualError !== null && annualError !== void 0 ? annualError : monthlyError) !== null && _ref !== void 0 ? _ref : oneTimeFeeError,
    loading: annualLoading || monthlyLoading || oneTimeFeeLoading
  };
};

/**
 * Returns the one-time fee pricing for a list of products (e.g., onboarding services).
 *
 * @param productApiNames - Array of product API names to get pricing for
 * @param quantity - The quantity for each product (defaults to 1)
 * @returns The pricing data with fullPrice and calculatedPrice for each product
 */
export const useOneTimeFeePricingForProducts = ({
  productApiNames,
  quantity = 1
}) => {
  const productsToPurchase = productApiNames.map(productApiName => ({
    productApiName,
    quantity
  }));
  const {
    products,
    currencyCode,
    error,
    loading
  } = useGrowthPopPurchaseOptions({
    productsToPurchase
  });
  const pricingData = useMemo(() => {
    let totalFullPrice = 0;
    let totalCalculatedPrice = 0;
    productApiNames.forEach(productApiName => {
      var _product$purchaseOpti2;
      if (loading) {
        return undefined;
      }
      if (!products) {
        return null;
      }
      const product = products[productApiName];
      const oneTimeFeeOption = product === null || product === void 0 || (_product$purchaseOpti2 = product.purchaseOptions) === null || _product$purchaseOpti2 === void 0 ? void 0 : _product$purchaseOpti2.ONE_TIME_FEE;
      if (oneTimeFeeOption) {
        var _oneTimeFeeOption$dis;
        const fullPrice = oneTimeFeeOption.priceForCommittedPeriod ? Number(oneTimeFeeOption.priceForCommittedPeriod) : 0;

        // If there are discounts, calculate the discounted price
        let calculatedPrice = fullPrice;
        if (((_oneTimeFeeOption$dis = oneTimeFeeOption.discounts) === null || _oneTimeFeeOption$dis === void 0 ? void 0 : _oneTimeFeeOption$dis.length) > 0) {
          // Apply discounts - sum up all discount amounts
          const totalDiscountAmount = oneTimeFeeOption.discounts.reduce((sum, discount) => {
            const discountAmount = discount.discountedAmountPerPurchase ? Number(discount.discountedAmountPerPurchase) : 0;
            return sum + discountAmount;
          }, 0);
          calculatedPrice = Math.max(0, fullPrice - totalDiscountAmount);
        }
        totalFullPrice += fullPrice;
        totalCalculatedPrice += calculatedPrice;
      }
    });
    return {
      oneTimeFee: {
        fullPrice: totalFullPrice,
        calculatedPrice: totalCalculatedPrice
      }
    };
  }, [loading, products, productApiNames]);
  return {
    pricingData,
    currencyCode,
    loading,
    error
  };
};

/**
 * TERM SELECTOR HOOKS
 */

const filterOutInvalidTerms = ({
  loading,
  terms
}) => {
  return {
    annual: {
      commitment: terms.annual.commitment === 'ANNUAL' || terms.annual.commitment === 'MONTHLY' ? terms.annual.commitment : loading ? undefined : null,
      payment: terms.annual.payment === 'ANNUAL' || terms.annual.payment === 'MONTHLY' ? terms.annual.payment : loading ? undefined : null
    },
    monthly: {
      commitment: terms.monthly.commitment === 'ANNUAL' || terms.monthly.commitment === 'MONTHLY' ? terms.monthly.commitment : loading ? undefined : null,
      payment: terms.monthly.payment === 'ANNUAL' || terms.monthly.payment === 'MONTHLY' ? terms.monthly.payment : loading ? undefined : null
    }
  };
};
const getAvailableTermsForProductApiName = ({
  growthPurchaseOptionsProducts,
  productApiName,
  recurringTermsOnly = false
}) => {
  var _growthPurchaseOption, _growthPurchaseOption2, _growthPurchaseOption3, _growthPurchaseOption4, _growthPurchaseOption5;
  const availableTerms = growthPurchaseOptionsProducts !== null && growthPurchaseOptionsProducts !== void 0 && (_growthPurchaseOption = growthPurchaseOptionsProducts[productApiName]) !== null && _growthPurchaseOption !== void 0 && _growthPurchaseOption.purchaseOptions ? {
    annual: {
      commitment: (growthPurchaseOptionsProducts === null || growthPurchaseOptionsProducts === void 0 || (_growthPurchaseOption2 = growthPurchaseOptionsProducts[productApiName]) === null || _growthPurchaseOption2 === void 0 || (_growthPurchaseOption2 = _growthPurchaseOption2.purchaseOptions) === null || _growthPurchaseOption2 === void 0 || (_growthPurchaseOption2 = _growthPurchaseOption2.ANNUAL) === null || _growthPurchaseOption2 === void 0 ? void 0 : _growthPurchaseOption2.commitmentTerm) || null,
      payment: (growthPurchaseOptionsProducts === null || growthPurchaseOptionsProducts === void 0 || (_growthPurchaseOption3 = growthPurchaseOptionsProducts[productApiName]) === null || _growthPurchaseOption3 === void 0 || (_growthPurchaseOption3 = _growthPurchaseOption3.purchaseOptions) === null || _growthPurchaseOption3 === void 0 || (_growthPurchaseOption3 = _growthPurchaseOption3.ANNUAL) === null || _growthPurchaseOption3 === void 0 ? void 0 : _growthPurchaseOption3.paymentTerm) || null
    },
    monthly: {
      commitment: (growthPurchaseOptionsProducts === null || growthPurchaseOptionsProducts === void 0 || (_growthPurchaseOption4 = growthPurchaseOptionsProducts[productApiName]) === null || _growthPurchaseOption4 === void 0 || (_growthPurchaseOption4 = _growthPurchaseOption4.purchaseOptions) === null || _growthPurchaseOption4 === void 0 || (_growthPurchaseOption4 = _growthPurchaseOption4.MONTHLY) === null || _growthPurchaseOption4 === void 0 ? void 0 : _growthPurchaseOption4.commitmentTerm) || null,
      payment: (growthPurchaseOptionsProducts === null || growthPurchaseOptionsProducts === void 0 || (_growthPurchaseOption5 = growthPurchaseOptionsProducts[productApiName]) === null || _growthPurchaseOption5 === void 0 || (_growthPurchaseOption5 = _growthPurchaseOption5.purchaseOptions) === null || _growthPurchaseOption5 === void 0 || (_growthPurchaseOption5 = _growthPurchaseOption5.MONTHLY) === null || _growthPurchaseOption5 === void 0 ? void 0 : _growthPurchaseOption5.paymentTerm) || null
    }
  } : null;
  const filteredAvailableTerms = recurringTermsOnly && availableTerms ? filterOutInvalidTerms({
    loading: false,
    terms: availableTerms
  }) : availableTerms;
  return recurringTermsOnly ? filteredAvailableTerms : availableTerms;
};
const getAvailableTermForTermLength = ({
  availableTerms,
  termLength
}) => {
  var _availableTerms, _availableTerms2;
  if (!availableTerms) {
    return {
      commitmentTerm: null,
      paymentTerm: null
    };
  }
  return {
    commitmentTerm: (_availableTerms = availableTerms[termLength.toLowerCase()]) === null || _availableTerms === void 0 ? void 0 : _availableTerms.commitment,
    paymentTerm: (_availableTerms2 = availableTerms[termLength.toLowerCase()]) === null || _availableTerms2 === void 0 ? void 0 : _availableTerms2.payment
  };
};
export const useBulkAvailableTermsForProductApiNamesByTermLength = ({
  productApiNames,
  recurringTermsOnly = false,
  termLength,
  skip
}) => {
  const {
    products,
    error,
    loading,
    currencyCode
  } = useGrowthPopPurchaseOptions({
    productsToPurchase: productApiNames.map(productApiName => ({
      productApiName,
      quantity: 1
    })),
    skip
  });
  const availableTermsByProductApiName = {};
  if (products !== undefined && products !== null) {
    for (const productApiName of Object.keys(products)) {
      availableTermsByProductApiName[productApiName] = getAvailableTermForTermLength({
        availableTerms: getAvailableTermsForProductApiName({
          growthPurchaseOptionsProducts: products,
          productApiName,
          recurringTermsOnly
        }),
        termLength
      });
    }
  }
  return {
    availableTermsByProductApiName,
    loading,
    error,
    currencyCode
  };
};
export const useGetAvailableTerms = ({
  productApiName,
  quantity = 1,
  recurringTermsOnly = false
}) => {
  const {
    products,
    error,
    loading,
    currencyCode
  } = useGrowthPopPurchaseOptions({
    productsToPurchase: productApiName ? [{
      productApiName,
      quantity
    }] : [],
    skip: !productApiName
  });
  if (!productApiName || !products || loading || error) {
    return {
      availableTerms: loading ? undefined : null,
      error,
      loading,
      currencyCode
    };
  }
  return {
    availableTerms: getAvailableTermsForProductApiName({
      growthPurchaseOptionsProducts: products,
      productApiName,
      recurringTermsOnly
    }),
    error,
    loading,
    currencyCode
  };
};
export const useAvailableTermsForBulkProductApiNames = ({
  productsToPurchase,
  recurringTermsOnly = false
}) => {
  const {
    products,
    error,
    loading,
    currencyCode
  } = useGrowthPopPurchaseOptions({
    productsToPurchase
  });
  if (loading) {
    return {
      availableTermsByProductApiName: undefined,
      loading,
      error: null
    };
  }
  if (!products) {
    return {
      availableTermsByProductApiName: null,
      loading,
      error
    };
  }
  const availableTermsByProductApiName = {};
  for (const {
    productApiName
  } of productsToPurchase) {
    availableTermsByProductApiName[productApiName] = getAvailableTermsForProductApiName({
      growthPurchaseOptionsProducts: products,
      productApiName,
      recurringTermsOnly
    });
  }
  return {
    availableTermsByProductApiName,
    currencyCode,
    loading,
    error
  };
};
export const useGetAvailableTermsForTermSelector = ({
  productApiName,
  quantity = 1
}) => {
  var _availableTerms$annua, _availableTerms$annua2, _availableTerms$month, _availableTerms$month2;
  const {
    availableTerms,
    error,
    loading
  } = useGetAvailableTerms({
    productApiName,
    quantity,
    recurringTermsOnly: true
  });
  const shouldShowTermSelector = !!(availableTerms !== null && availableTerms !== void 0 && (_availableTerms$annua = availableTerms.annual) !== null && _availableTerms$annua !== void 0 && _availableTerms$annua.commitment && availableTerms !== null && availableTerms !== void 0 && (_availableTerms$annua2 = availableTerms.annual) !== null && _availableTerms$annua2 !== void 0 && _availableTerms$annua2.payment && availableTerms !== null && availableTerms !== void 0 && (_availableTerms$month = availableTerms.monthly) !== null && _availableTerms$month !== void 0 && _availableTerms$month.commitment && availableTerms !== null && availableTerms !== void 0 && (_availableTerms$month2 = availableTerms.monthly) !== null && _availableTerms$month2 !== void 0 && _availableTerms$month2.payment);
  return {
    availableTerms: availableTerms,
    error,
    loading,
    shouldShowTermSelector
  };
};

/**
 * HOOKS FOR PURCHASE MOTIONS
 */

export const useGrowthPopForPurchaseMotions = ({
  productApiName,
  quantity = 1,
  term = 'ANNUAL'
}) => {
  const {
    availableTerms: availableTermsForRecurringTerms,
    error,
    loading,
    currencyCode
  } = useGetAvailableTerms({
    productApiName,
    quantity,
    recurringTermsOnly: true
  });
  const availableTerms = availableTermsForRecurringTerms;

  // Return null if product is unavailable (availableTerms is null)

  if (!productApiName || !availableTerms) {
    return {
      commitmentTerm: loading ? undefined : null,
      paymentTerm: loading ? undefined : null,
      currencyCode,
      error,
      loading
    };
  }
  const {
    commitmentTerm,
    paymentTerm
  } = getAvailableTermForTermLength({
    availableTerms,
    termLength: term
  });
  return {
    commitmentTerm,
    paymentTerm,
    currencyCode,
    error,
    loading
  };
};

/**
 * Calculates the discount percentage between monthly and annual terms.
 *
 * Compares the monthly price of the MONTHLY term vs ANNUAL term to determine
 * how much you save by choosing annual billing over monthly billing.
 *
 * @param productApiName - The product API name to query
 * @param quantity - The quantity of the product to purchase (defaults to 1)
 * @returns Object containing:
 *   - discountPercentage: The percentage discount (null if no discount or data unavailable)
 *   - monthlyTermPrice: The monthly price when paying monthly
 *   - annualTermPrice: The monthly price when paying annually
 *   - hasAnnualDiscount: Boolean indicating if there's a discount
 *   - currencyCode: The currency code
 *   - error: Any error from the API call
 *   - loading: Loading state
 *
 * @example
 * ```tsx
 * const { discountPercentage, hasAnnualDiscount, loading, error } = useAnnualDiscountPercentage({
 *   productApiName: 'marketing-hub-professional',
 *   quantity: 1,
 * });
 *
 * if (loading) return <div>Loading...</div>;
 * if (error) return <div>Error loading pricing</div>;
 *
 * return (
 *   <div>
 *     {hasAnnualDiscount && discountPercentage && (
 *       <span>Save {discountPercentage}% with annual billing!</span>
 *     )}
 *   </div>
 * );
 * ```
 */
export const useAnnualDiscountPercentage = ({
  productApiName,
  quantity = 1
}) => {
  const {
    products,
    currencyCode,
    error,
    loading
  } = useGrowthPopPurchaseOptions({
    productsToPurchase: productApiName ? [{
      productApiName,
      quantity
    }] : [],
    skip: !productApiName
  });
  const discountInfo = useMemo(() => {
    var _product$purchaseOpti3, _product$purchaseOpti4;
    if (loading) {
      return {
        discountPercentage: undefined,
        monthlyTermPrice: undefined,
        annualTermPrice: undefined,
        hasAnnualDiscount: false
      };
    }
    if (!products) {
      return {
        discountPercentage: null,
        monthlyTermPrice: null,
        annualTermPrice: null,
        hasAnnualDiscount: false
      };
    }
    if (!productApiName || !products[productApiName]) {
      return {
        discountPercentage: null,
        monthlyTermPrice: null,
        annualTermPrice: null,
        hasAnnualDiscount: false
      };
    }
    const product = products[productApiName];
    const monthlyOption = (_product$purchaseOpti3 = product.purchaseOptions) === null || _product$purchaseOpti3 === void 0 ? void 0 : _product$purchaseOpti3.MONTHLY;
    const annualOption = (_product$purchaseOpti4 = product.purchaseOptions) === null || _product$purchaseOpti4 === void 0 ? void 0 : _product$purchaseOpti4.ANNUAL;
    if (!monthlyOption || !annualOption) {
      return {
        discountPercentage: null,
        monthlyTermPrice: null,
        annualTermPrice: null,
        hasAnnualDiscount: false
      };
    }
    const monthlyTermPrice = monthlyOption.monthlyPrice ? Number(monthlyOption.monthlyPrice) : null;
    const annualTermPrice = annualOption.monthlyPrice ? Number(annualOption.monthlyPrice) : null;
    if (!monthlyTermPrice || !annualTermPrice) {
      return {
        discountPercentage: null,
        monthlyTermPrice,
        annualTermPrice,
        hasAnnualDiscount: false
      };
    }
    if (monthlyTermPrice <= annualTermPrice) {
      return {
        discountPercentage: null,
        monthlyTermPrice,
        annualTermPrice,
        hasAnnualDiscount: false
      };
    }
    const discountPercentage = (monthlyTermPrice - annualTermPrice) / monthlyTermPrice * 100;
    return {
      discountPercentage: Math.round(discountPercentage),
      monthlyTermPrice,
      annualTermPrice,
      hasAnnualDiscount: discountPercentage > 0
    };
  }, [loading, products, productApiName]);
  return Object.assign({}, discountInfo, {
    currencyCode,
    error,
    loading
  });
};

/**
 * SEATS PRICING HOOKS
 */
export const useAssociatedSeatsInfo = ({
  productApiName,
  quantity = 1,
  term = 'ANNUAL'
}) => {
  var _products$productApiN7;
  const {
    products,
    currencyCode,
    error,
    loading
  } = useGrowthPopPurchaseOptions({
    productsToPurchase: productApiName ? [{
      productApiName,
      quantity
    }] : [],
    skip: !productApiName
  });
  if (loading) {
    return {
      apiNameForSeat: undefined,
      seatMonthlyPrice: undefined,
      seatPriceForCommittedPeriod: undefined,
      currencyCode,
      error,
      loading
    };
  }
  if (!products) {
    return {
      apiNameForSeat: null,
      seatMonthlyPrice: null,
      seatPriceForCommittedPeriod: null,
      currencyCode,
      error,
      loading
    };
  }
  // Return null if loading is complete but product is unavailable
  const productUnavailable = !products[productApiName];
  const associatedSeatsInfo = productUnavailable ? null : (_products$productApiN7 = products[productApiName]) === null || _products$productApiN7 === void 0 || (_products$productApiN7 = _products$productApiN7.purchaseOptions) === null || _products$productApiN7 === void 0 || (_products$productApiN7 = _products$productApiN7[term]) === null || _products$productApiN7 === void 0 ? void 0 : _products$productApiN7.seat;
  const apiNameForSeat = productUnavailable ? null : associatedSeatsInfo === null || associatedSeatsInfo === void 0 ? void 0 : associatedSeatsInfo.productApiName;
  const seatMonthlyPrice = productUnavailable ? null : associatedSeatsInfo === null || associatedSeatsInfo === void 0 ? void 0 : associatedSeatsInfo.seatMonthlyPrice;
  const seatPriceForCommittedPeriod = productUnavailable ? null : associatedSeatsInfo === null || associatedSeatsInfo === void 0 ? void 0 : associatedSeatsInfo.seatPriceForCommittedPeriod;
  return {
    apiNameForSeat,
    seatMonthlyPrice,
    seatPriceForCommittedPeriod,
    currencyCode,
    error,
    loading
  };
};
export const useGrowthPurchaseOptionsForBundle = ({
  bundleProducts,
  targetPromotionKeys,
  skip: skipFromCaller = false
}) => {
  var _discountedProduct$di, _discountedProduct$li;
  const currencyCode = useDisplayedCurrencyCode();
  const {
    data,
    error,
    loading
  } = useQuery(GET_IN_APP_GROWTH_PURCHASE_OPTIONS_FOR_CART_QUERY, {
    variables: {
      products: bundleProducts.map(p => ({
        productApiName: p.productApiName,
        skuQuantity: p.quantity
      })),
      currencyCode
    },
    skip: skipFromCaller || !currencyCode
  });
  const purchaseOptions = useMemo(() => {
    var _data$result$purchase;
    return (_data$result$purchase = data === null || data === void 0 ? void 0 : data.result.purchaseOptionsResponse.purchaseOptions) !== null && _data$result$purchase !== void 0 ? _data$result$purchase : [];
  }, [data]);

  // Find the ACAP term (annual commitment + annual payment)
  const acapTerm = useMemo(() => {
    var _acapOption$orderedTe, _acapOption$orderedTe2;
    const acapOption = purchaseOptions.find(opt => opt.commitmentTerm === 12 && opt.paymentTerm === 12);
    return (_acapOption$orderedTe = acapOption === null || acapOption === void 0 || (_acapOption$orderedTe2 = acapOption.orderedTerms) === null || _acapOption$orderedTe2 === void 0 ? void 0 : _acapOption$orderedTe2[0]) !== null && _acapOption$orderedTe !== void 0 ? _acapOption$orderedTe : null;
  }, [purchaseOptions]);

  // Within the ACAP term, find the product whose discount matches targetPromotionKeys
  const discountedProduct = useMemo(() => {
    var _Object$values$find, _acapTerm$recurringPr;
    if (!acapTerm || !targetPromotionKeys) {
      return null;
    }
    return (_Object$values$find = Object.values((_acapTerm$recurringPr = acapTerm.recurringPricedProductBySkuUuid) !== null && _acapTerm$recurringPr !== void 0 ? _acapTerm$recurringPr : {}).find(product => {
      var _product$discounts;
      return (_product$discounts = product.discounts) === null || _product$discounts === void 0 ? void 0 : _product$discounts.some(d => targetPromotionKeys.includes(d.promotionId));
    })) !== null && _Object$values$find !== void 0 ? _Object$values$find : null;
  }, [acapTerm, targetPromotionKeys]);
  let annualDiscountedMonthlyPrice;
  if (loading) {
    annualDiscountedMonthlyPrice = undefined;
  } else if (discountedProduct !== null && discountedProduct !== void 0 && (_discountedProduct$di = discountedProduct.discountedPrice) !== null && _discountedProduct$di !== void 0 && _discountedProduct$di.monthlyPrice) {
    annualDiscountedMonthlyPrice = Number(discountedProduct.discountedPrice.monthlyPrice);
  } else {
    annualDiscountedMonthlyPrice = null;
  }
  let annualListMonthlyPrice;
  if (loading) {
    annualListMonthlyPrice = undefined;
  } else if (discountedProduct !== null && discountedProduct !== void 0 && (_discountedProduct$li = discountedProduct.listPrice) !== null && _discountedProduct$li !== void 0 && _discountedProduct$li.monthlyPrice) {
    annualListMonthlyPrice = Number(discountedProduct.listPrice.monthlyPrice);
  } else {
    annualListMonthlyPrice = null;
  }

  // Collect all promotion IDs across all terms
  const promotionIds = useMemo(() => {
    if (loading) {
      return undefined;
    }
    const ids = [];
    for (const option of purchaseOptions) {
      for (const term of (_option$orderedTerms = option.orderedTerms) !== null && _option$orderedTerms !== void 0 ? _option$orderedTerms : []) {
        var _option$orderedTerms;
        for (const product of Object.values((_term$recurringPriced = term.recurringPricedProductBySkuUuid) !== null && _term$recurringPriced !== void 0 ? _term$recurringPriced : {})) {
          var _term$recurringPriced;
          for (const discount of (_product$discounts2 = product.discounts) !== null && _product$discounts2 !== void 0 ? _product$discounts2 : []) {
            var _product$discounts2;
            if (discount.promotionId) {
              ids.push(discount.promotionId);
            }
          }
        }
      }
    }
    return ids;
  }, [loading, purchaseOptions]);
  return {
    annualDiscountedMonthlyPrice,
    annualListMonthlyPrice,
    promotionIds,
    error,
    loading
  };
};